import React from "react";
import Style from "./Home.module.scss";
import classNames from "classnames";
import EmojiBullet from "./EmojiBullet";
import SocialIcon from "./SocialIcon";
import { Box, Typography, Button, Stack, Container, Grid } from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import PersonIcon from "@mui/icons-material/Person";
import { info } from "../../info/Info";
import { Helmet } from "react-helmet-async";

export default function Home({ innerRef }) {
    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const yCoordinate = element.getBoundingClientRect().top + window.pageYOffset;
            const yOffset = -75;
            window.scrollTo({ top: yCoordinate + yOffset, behavior: "smooth" });
        }
    };

    return (
        <>
            <Helmet>
                <title>Filippos Kokkinos, Ph.D. | AI Research Scientist</title>
                <meta
                    name="description"
                    content="Personal website of Filippos Kokkinos, AI Research Scientist at Meta. Researching Multimodal Reasoning, Foundation Models, and 3D Generative AI."
                />
            </Helmet>
            <Container
                maxWidth="lg"
                sx={{
                    minHeight: "calc(100vh - 120px)",
                    display: "flex",
                    alignItems: "center",
                    py: { xs: 4, md: 6 }
                }}
                ref={innerRef}
                id="home"
            >
                <Grid container spacing={4} alignItems="center" justifyContent="center">
                    {/* Avatar Column */}
                    <Grid item xs={12} md={5} display="flex" flexDirection="column" alignItems="center">
                        <Box
                            className={classNames(Style.avatar, Style.shadowed)}
                            alt="Filippos Kokkinos - AI Research Scientist"
                            style={{ background: info.gradient }}
                            component="img"
                            src={info.selfPortrait}
                            sx={{
                                width: { xs: "220px", sm: "260px", md: "300px" },
                                height: { xs: "220px", sm: "260px", md: "300px" },
                                borderRadius: "50%",
                                p: "0.5rem",
                                objectFit: "cover",
                                boxShadow: "0 12px 36px rgba(0, 0, 0, 0.15)",
                                transition: "transform 0.3s ease",
                                "&:hover": {
                                    transform: "scale(1.02)"
                                }
                            }}
                        />
                    </Grid>

                    {/* Bio / Content Column */}
                    <Grid item xs={12} md={7}>
                        <Box textAlign={{ xs: "center", md: "left" }}>
                            <Typography
                                variant="h3"
                                component="h1"
                                fontWeight={800}
                                sx={{ letterSpacing: "-0.02em", mb: 1 }}
                            >
                                Hi, I'm{" "}
                                <span
                                    style={{
                                        background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent"
                                    }}
                                >
                                    {info.firstName} {info.lastName}
                                </span>
                            </Typography>

                            <Typography variant="h5" component="h2" fontWeight={600} color="text.secondary" sx={{ mb: 2.5 }}>
                                {info.position}
                            </Typography>

                            {/* Mini Bio List */}
                            <Box component="ul" sx={{ p: 0, listStyle: "none", mb: 3 }}>
                                {info.miniBio.map((bio, index) => (
                                    <EmojiBullet key={index} icon={bio.icon} text={bio.text} />
                                ))}
                            </Box>

                            {/* Action Buttons */}
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.5}
                                justifyContent={{ xs: "center", md: "flex-start" }}
                                flexWrap="wrap"
                                sx={{ mb: 3 }}
                            >
                                <Button
                                    variant="contained"
                                    size="medium"
                                    startIcon={<ArticleIcon />}
                                    onClick={() => scrollToSection("portfolio")}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "10px",
                                        px: 2.5,
                                        py: 1,
                                        fontWeight: 600,
                                        background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
                                        boxShadow: "0 4px 14px rgba(30, 64, 175, 0.35)",
                                        "&:hover": {
                                            background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)"
                                        }
                                    }}
                                >
                                    Publications
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    startIcon={<NewspaperIcon />}
                                    onClick={() => scrollToSection("news")}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "10px",
                                        px: 2.5,
                                        py: 1,
                                        fontWeight: 600
                                    }}
                                >
                                    Recent News
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    startIcon={<PersonIcon />}
                                    onClick={() => scrollToSection("about")}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "10px",
                                        px: 2.5,
                                        py: 1,
                                        fontWeight: 600
                                    }}
                                >
                                    About & Research
                                </Button>
                            </Stack>

                            {/* Social Icons */}
                            <Box
                                display="flex"
                                gap={2}
                                justifyContent={{ xs: "center", md: "flex-start" }}
                                fontSize={{ xs: "1.8rem", md: "2.2rem" }}
                            >
                                {info.socials.map((social, index) => (
                                    <SocialIcon key={index} link={social.link} icon={social.icon} label={social.label} />
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}
